import { NATIVE_INTAKE_PROFILE_VERSION } from '@ohf/core/shared';
import copy from '../copy/en/signing.json' with { type: 'json' };
import { formatCopy } from '../copy/format.js';
import { DATA_STAGES, JOURNEY_STEPS } from './flow.js';
import { collectNativeIntake, configureNativeIntake, renderNativeReview } from './native-intake.js';
import { createReviewReturn } from './review-return.js';
import { createAdditionalOfficeFlow } from './additional-office.js';
import { initializeCarriedFieldEditing } from './carried-field-editing.js';

/** Query-only presentation state. It never grants signing authority. */
export function isNavigationPreviewRequested(search: string, formKey: string): boolean {
  const query = new URLSearchParams(search);
  const values = query.getAll('preview');
  return formKey === 'agreement' && values.length === 1 && values[0] === '1'
    && ![...query.keys()].some(key => key.startsWith('preview['));
}

interface NavigationPreviewOptions {
  form: HTMLFormElement;
  showDataStage(index: number, focus?: boolean): void;
  setJourneyStep(index: number): void;
  revealAndFocus(section: HTMLElement, headingSelector: string): void;
}

/** A disposable, local-only browser for the current native questionnaire. */
export function initializeNavigationPreview(options: NavigationPreviewOptions): () => void {
  const { form, showDataStage, setJourneyStep, revealAndFocus } = options;
  const doc = form.ownerDocument;
  const find = <T extends HTMLElement = HTMLElement>(selector: string): T => doc.querySelector<T>(selector)!;
  const listeners: (() => void)[] = [];
  function listen(target: EventTarget, type: string, listener: EventListener): void {
    target.addEventListener(type, listener);
    listeners.push(() => target.removeEventListener(type, listener));
  }
  let currentStep = 0;
  let carriedEditing: ReturnType<typeof initializeCarriedFieldEditing> | null = null;
  const collect = () => { carriedEditing?.settle(); return collectNativeIntake(form); };
  const review = find('#reviewSection');
  const success = find('#successSection');
  const rail = find('.progress-rail');
  const reviewAnswers = find('#reviewAnswers');
  const reviewFields = find<HTMLFieldSetElement>('#reviewFields');
  const signBlock = find('#signBlock');
  const consentPanel = find('.consent-panel');
  const consent = find<HTMLInputElement>('#consentCheck');
  const pdfFrame = find<HTMLIFrameElement>('#pdfFrame');
  const documentHeading = find('.document-heading');
  const reviewIntro = find('.review-header p:last-child');
  const reviewTitle = find('#reviewTitle');
  const reviewKicker = find('.review-header .stage-kicker');
  const reviewHeader = find('.review-header');
  const signingIntroduction = find('#signingIntroduction');
  const originalReviewTitle = reviewTitle.textContent;
  const originalReviewKicker = reviewKicker.textContent;

  doc.body.dataset.navigationPreview = 'true';
  configureNativeIntake(form, NATIVE_INTAKE_PROFILE_VERSION);
  const reviewReturn = createReviewReturn(form, copy.updateReview, copy.updatingReview);
  find<HTMLFieldSetElement>('#intakeFields').disabled = false;
  find('.form-surface').setAttribute('aria-busy', 'false');
  find('#sessionPanel').classList.add('preview-notice');
  find('#sessionPanel').setAttribute('aria-label', copy.navigationPreviewTitle);
  find('#sessionStatus').textContent = copy.navigationPreviewTitle;
  const notice = doc.createElement('p');
  notice.className = 'hint';
  notice.textContent = copy.navigationPreviewNotice;
  find('#sessionPanel').append(notice);
  for (const guidance of doc.querySelectorAll('.hero > p:last-child, .embed-save-guidance')) guidance.textContent = copy.navigationPreviewSaveGuidance;
  find('.secure-note').textContent = copy.navigationPreviewTitle;
  find('.footer-inner > span:last-child').classList.add('hidden');
  find('#resumeActions').classList.add('hidden');
  for (const id of ['retrySession', 'recoverDocument', 'copyResumeLink']) {
    const button = find<HTMLButtonElement>(`#${id}`);
    button.disabled = true;
    button.classList.add('hidden');
  }
  const previewButton = find<HTMLButtonElement>('#previewBtn');
  previewButton.querySelector('.btn-label')!.textContent = copy.navigationPreviewReviewAnswers;
  for (const id of ['openPreview', 'downloadPreview', 'downloadAgain']) {
    const link = find<HTMLAnchorElement>(`#${id}`);
    link.removeAttribute('href');
    link.classList.add('hidden');
    link.setAttribute('aria-disabled', 'true');
  }
  pdfFrame.removeAttribute('src');
  pdfFrame.classList.add('hidden');
  const placeholder = doc.createElement('p');
  placeholder.id = 'navigationPreviewPdf';
  placeholder.className = 'preview-document-placeholder';
  placeholder.setAttribute('role', 'note');
  placeholder.textContent = copy.navigationPreviewPdfPlaceholder;
  pdfFrame.before(placeholder);
  for (const element of [documentHeading.nextElementSibling, find('.preview-actions')]) {
    // The ordinary PDF instructions and actions do not describe a local preview.
    element?.classList.add('hidden');
  }
  const pdfHint = find('.preview-actions').nextElementSibling;
  pdfHint?.classList.add('hidden');
  consent.checked = false;
  consent.disabled = true;
  find<HTMLInputElement>('#typedName').disabled = true;
  find<HTMLButtonElement>('#finalizeBtn').disabled = true;
  find<HTMLButtonElement>('#clearSig').disabled = true;
  find<HTMLButtonElement>('#uploadSignature').disabled = true;
  find<HTMLButtonElement>('#photoSignature').disabled = true;
  find<HTMLInputElement>('#signatureImageFile').disabled = true;
  find('#sigPad').setAttribute('aria-disabled', 'true');
  listen(consent, 'change', () => { consent.checked = false; });
  for (const selector of ['.success-mark', '.completion-summary', '.document-evidence', '#emailNote']) find(selector).classList.add('hidden');
  find('#successTitle').textContent = copy.navigationPreviewCompleteTitle;
  find('#completionNote').textContent = copy.navigationPreviewCompleteNotice;
  find('#outSubmission').textContent = '';
  find('#outHash').textContent = '';

  const additionalOffice = createAdditionalOfficeFlow({
    form, preview: true,
    async onStart() {
      carriedEditing?.dispose();
      reviewReturn.setActive(false);
      showStep(0);
      carriedEditing = initializeCarriedFieldEditing(form);
      return true;
    },
    onReview: () => showStep(DATA_STAGES.length),
    onEditLocation: () => {
      if (currentStep >= DATA_STAGES.length) reviewReturn.setActive(true);
      showStep(0, false);
      const address = form.querySelector<HTMLInputElement>('[name="intakeProfile.locations.addressLine1"]');
      address?.scrollIntoView({ block: 'center' });
      address?.focus({ preventScroll: true });
    },
  });
  listen(form, 'input', () => additionalOffice.changed(collect()));
  listen(form, 'change', () => additionalOffice.changed(collect()));

  const stepButtons = JOURNEY_STEPS.map((step, index) => {
    const item = find(`#journey-step-${index + 1}`);
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'preview-step-button';
    button.dataset.previewStep = String(index);
    button.setAttribute('aria-label', formatCopy(copy.navigationPreviewStepLabel, { step: step.label }));
    button.setAttribute('aria-controls', index < DATA_STAGES.length ? find(`[data-data-stage="${index}"]`).id : index === 7 ? success.id : review.id);
    button.append(...Array.from(item.childNodes));
    item.append(button);
    return button;
  });
  const actions = doc.createElement('div');
  actions.className = 'stage-actions preview-stage-actions hidden';
  const back = doc.createElement('button');
  back.type = 'button'; back.className = 'btn btn-ghost'; back.textContent = copy.navigationPreviewBack;
  const next = doc.createElement('button');
  next.type = 'button'; next.className = 'btn btn-primary'; next.textContent = copy.navigationPreviewNext;
  actions.append(back, next);
  find('.form-surface').append(actions);

  function showStep(index: number, focus = true): void {
    carriedEditing?.settle();
    currentStep = Math.min(JOURNEY_STEPS.length - 1, Math.max(0, index));
    additionalOffice.setVisible(currentStep !== JOURNEY_STEPS.length - 1);
    if (currentStep !== JOURNEY_STEPS.length - 1) additionalOffice.changed(collect());
    if (currentStep >= DATA_STAGES.length) reviewReturn.setActive(false);
    actions.classList.toggle('hidden', currentStep < DATA_STAGES.length);
    next.classList.toggle('hidden', currentStep === JOURNEY_STEPS.length - 1);
    if (currentStep < DATA_STAGES.length) showDataStage(currentStep, focus);
    else {
      const signing = currentStep === DATA_STAGES.length + 1;
      const completion = currentStep === JOURNEY_STEPS.length - 1;
      form.classList.add('hidden');
      review.classList.toggle('hidden', completion);
      review.setAttribute('aria-labelledby', signing ? 'signTitle' : 'reviewTitle');
      success.classList.toggle('hidden', !completion);
      reviewFields.disabled = false;
      reviewHeader.classList.toggle('hidden', signing);
      signingIntroduction.classList.toggle('hidden', !signing);
      reviewAnswers.classList.toggle('hidden', signing);
      documentHeading.classList.toggle('hidden', signing);
      placeholder.classList.toggle('hidden', signing);
      consentPanel.classList.toggle('hidden', !signing);
      signBlock.classList.toggle('hidden', !signing);
      find('#expectedSignerName').textContent = collect().signerName?.trim() || copy.navigationPreviewSignerName;
      reviewTitle.textContent = signing ? copy.navigationPreviewSignTitle : originalReviewTitle;
      reviewKicker.textContent = signing ? copy.step7Sign : originalReviewKicker;
      reviewIntro.textContent = signing ? copy.navigationPreviewSigningDisabled : copy.navigationPreviewReviewNotice;
      if (!completion && !signing) renderNativeReview(reviewAnswers, collect());
      if (completion) additionalOffice.completed(collect(), '', '', true);
      setJourneyStep(currentStep);
      if (focus) revealAndFocus(completion ? success : review, signing ? '#signTitle' : 'h2');
    }
    stepButtons.forEach((button, step) => {
      button.parentElement!.classList.remove('done');
      button.parentElement!.removeAttribute('aria-current');
      if (step === currentStep) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    if (focus) stepButtons[currentStep]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  listen(rail, 'click', event => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-preview-step]');
    if (button && rail.contains(button)) showStep(Number(button.dataset.previewStep));
  });
  listen(form, 'click', event => {
    const target = event.target as Element;
    if (target.closest('[data-stage-next]')) showStep(currentStep + 1);
    else if (target.closest('[data-stage-back]')) showStep(currentStep - 1);
  });
  listen(form, 'submit', event => {
    event.preventDefault();
    const editedStage = reviewReturn.active ? currentStep : undefined;
    showStep(editedStage !== undefined ? DATA_STAGES.length : currentStep < DATA_STAGES.length ? currentStep + 1 : DATA_STAGES.length);
    const card = editedStage !== undefined ? reviewAnswers.querySelector<HTMLDetailsElement>(`[data-review-stage="${editedStage}"]`) : null;
    if (card) { card.open = true; revealAndFocus(card, 'summary'); }
  });
  listen(find('#editFormBtn'), 'click', () => { reviewReturn.setActive(true); showStep(0); });
  listen(find('#editSignerDetails'), 'click', () => {
    reviewReturn.setActive(true);
    showStep(1);
    revealAndFocus(find('[data-data-stage="1"]'), '[name="signerName"]');
  });
  listen(reviewAnswers, 'click', event => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-edit-stage]');
    if (button) { reviewReturn.setActive(true); showStep(Number(button.dataset.editStage)); }
  });
  listen(back, 'click', () => showStep(currentStep - 1));
  listen(next, 'click', () => showStep(currentStep + 1));
  showStep(0, false);
  return () => {
    carriedEditing?.dispose();
    additionalOffice.dispose();
    reviewReturn.dispose();
    listeners.forEach(remove => remove());
    delete doc.body.dataset.navigationPreview;
  };
}
